import { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { getMuiTheme, getActiveThemeMode, setActiveThemeMode, type ThemeMode } from './config/theme';
import { queryClient } from './config/queryClient';
import { AuthProvider, ProtectedRoute, Login } from './features/auth';
import { AppErrorBoundary } from './shared/components/ErrorBoundary';
import { NotificationStack } from './shared/components/NotificationStack';
import { AppShell } from './features/shell/AppShell';
import { StandaloneViewer } from './features/multi-monitor/StandaloneViewer';

/**
 * Global theme change listener — components can call
 * window.dispatchEvent(new CustomEvent('brit:theme-change'))
 * to trigger a re-render with the new theme.
 */
function App() {
  const [themeMode, setThemeMode] = useState<ThemeMode>(getActiveThemeMode());

  const handleThemeChange = useCallback(() => {
    setThemeMode(getActiveThemeMode());
  }, []);

  useEffect(() => {
    window.addEventListener('brit:theme-change', handleThemeChange);
    return () => window.removeEventListener('brit:theme-change', handleThemeChange);
  }, [handleThemeChange]);

  // Expose a global helper for theme switching
  useEffect(() => {
    (window as unknown as Record<string, unknown>).__britSetTheme = (mode: ThemeMode) => {
      setActiveThemeMode(mode);
      window.dispatchEvent(new CustomEvent('brit:theme-change'));
    };
  }, []);

  const theme = getMuiTheme(themeMode);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <AppErrorBoundary>
          <AuthProvider>
            <Router>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route
                  path="/viewer-standalone"
                  element={
                    <ProtectedRoute>
                      <StandaloneViewer />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <AppShell />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Router>
          </AuthProvider>
        </AppErrorBoundary>
        <NotificationStack />
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
